import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { SupportDocForm } from "@/components/support-documents/SupportDocForm";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { useCompany } from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/documentos-soporte/new",
)({
  component: NewSupportDocPage,
});

function NewSupportDocPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewSupportDocContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewSupportDocContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const companyQuery = useCompany(companyId);

  if (companyQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (companyQuery.isError || !companyQuery.data) {
    return (
      <ErrorState
        title={t("supportDocs.list.error_title")}
        description={t("supportDocs.list.error_body")}
        onRetry={() => void companyQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="documento_soporte" />
      <div>
        <Link
          to="/companies/$companyId/documentos-soporte"
          params={{ companyId }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("supportDocs.new.back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t("supportDocs.new.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("supportDocs.new.subtitle")}
        </p>
      </div>
      <SupportDocForm companyId={companyId} company={companyQuery.data} />
    </div>
  );
}
